// src/pages/ContactPage.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  TextField,
  Button,
  MobileStepper,
  useTheme,
  Paper,
} from '@mui/material';
import {
  Email,
  WhatsApp,
  ArrowForward,
  ArrowBack,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
// import Navbar from '../components/Navbar';
// import Footer from '../components/Footer';
import FacebookFeed from '../components/FacebookFeed';

const contactDetails = [
  {
    icon: <Email fontSize="large" color="primary" />,
    label: 'Email Us',
    detail: 'lemda.webdev@gmail.com',
    href: 'mailto:lemda.webdev@gmail.com',
  },
  {
    icon: <WhatsApp fontSize="large" color="primary" />,
    label: 'WhatsApp',
    detail: '+94 770837053',
    href: 'https://wa.me/94770837053',
  },
];

export default function ContactPage() {
  const theme = useTheme();
  const [step, setStep] = useState(0);
  const [values, setValues] = useState({
    name: '',
    email: '',
    type: '',
    brief: '',
    budget: 500,
  });

  // Load Facebook SDK
  useEffect(() => {
    if (!document.getElementById('facebook-jssdk')) {
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src =
        'https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v17.0';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  const steps = [
    <Box key="step1" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label="Full Name"
        variant="filled"
        fullWidth
        value={values.name}
        onChange={(e) =>
          setValues((v) => ({ ...v, name: e.target.value }))
        }
      />
      <TextField
        label="Email Address"
        variant="filled"
        fullWidth
        value={values.email}
        onChange={(e) =>
          setValues((v) => ({ ...v, email: e.target.value }))
        }
      />
    </Box>,
    <Box key="step2" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        select
        label="Project Type"
        variant="filled"
        fullWidth
        SelectProps={{ native: true }}
        value={values.type}
        onChange={(e) =>
          setValues((v) => ({ ...v, type: e.target.value }))
        }
      >
        <option value="">– Select –</option>
        <option>Web App</option>
        <option>Mobile App</option>
        <option>AI Integration</option>
      </TextField>
      <Typography color="text.secondary">
        Budget: LKR {values.budget}
      </Typography>
      <input
        type="range"
        min="500"
        max="100000"
        step="500"
        value={values.budget}
        onChange={(e) =>
          setValues((v) => ({ ...v, budget: e.target.value }))
        }
        style={{ width: '100%' }}
      />
    </Box>,
    <Box key="step3">
      <TextField
        label="Project Brief"
        variant="filled"
        multiline
        rows={4}
        fullWidth
        value={values.brief}
        onChange={(e) =>
          setValues((v) => ({ ...v, brief: e.target.value }))
        }
      />
    </Box>,
  ];

  const next = () => setStep((s) => Math.min(s + 1, steps.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  return (
    <>
      {/* <Navbar /> */}

      {/* Hero */}
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          textAlign: 'center',
          bgcolor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
        }}
        component={motion.div}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" fontWeight={700}>
            Get In Touch
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.8, mt: 2 }}>
            Let’s turn your idea into reality—fast.
          </Typography>
        </Container>
      </Box>

      <Container sx={{ py: { xs: 4, md: 8 } }}>
        {/* Contact Cards */}
        <Grid container spacing={4} justifyContent="center" sx={{ mb: 6 }}>
          {contactDetails.map((c, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <motion.div
                whileHover={{ y: -6 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <Card
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    boxShadow: 2,
                    borderRadius: 3,
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 6 },
                  }}
                  onClick={() => window.open(c.href, '_blank')}
                >
                  {c.icon}
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    sx={{ mt: 1 }}
                  >
                    {c.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    {c.detail}
                  </Typography>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Quote Form */}
        <Box sx={{ maxWidth: 600, mx: 'auto', mb: 6 }}>
          {steps[step]}
          <MobileStepper
            variant="progress"
            steps={steps.length}
            position="static"
            activeStep={step}
            nextButton={
              <Button
                endIcon={<ArrowForward />}
                onClick={next}
                disabled={step === steps.length - 1}
              >
                {step === steps.length - 1 ? 'Submit' : 'Next'}
              </Button>
            }
            backButton={
              <Button
                startIcon={<ArrowBack />}
                onClick={back}
                disabled={step === 0}
              >
                Back
              </Button>
            }
            sx={{ mt: 3 }}
          />
        </Box>

        {/* Map, QR & Facebook */}
        <Grid container spacing={4} sx={{ mt: 8, alignItems: 'flex-start' }}>
          {/* Google Map */}
          <Grid item xs={12} md={6}>
            <Box
              component="iframe"
              src="https://maps.google.com/maps?q=World%20Trade%20Center%20Colombo&z=15&output=embed"
              sx={{
                width: '100%',
                height: 300,
                border: 0,
                borderRadius: 2,
                boxShadow: 2,
              }}
            />
          </Grid>

          {/* QR + Facebook Plugin */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
              {/* QR */}
              <Box textAlign="center" sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Scan to Verify
                </Typography>
                <Box
                  component="img"
                  src="/lemdaqr.png"
                  alt="Lemda vCard QR"
                  sx={{
                    width: 200,
                    mx: 'auto',
                    boxShadow: 2,
                    borderRadius: 2,
                    mb: 2,
                  }}
                />
              </Box>

              
            </Paper>
            
          </Grid>
           <FacebookFeed/>
        </Grid>
       
      </Container>

      {/* <Footer /> */}
    </>
  );
}
